import { useNavigate } from 'react-router-dom';
import { cn } from '@/libs/utils';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'use-intl';

interface GeneralErrorProps extends React.HTMLAttributes<HTMLDivElement> {
  minimal?: boolean;
}

// Fallback translations when intl context is not available
const fallbackTranslations: Record<string, string> = {
  'error.500_title': 'Something went wrong',
  'error.500_desc': 'An unexpected error occurred. Please try again later.',
  'error.go_back': 'Go back',
  'error.back_to_home': 'Back to home',
};

function GeneralErrorContent({
  className,
  minimal = false,
  translations,
}: GeneralErrorProps & { translations?: ReturnType<typeof useTranslations> }) {
  const navigate = useNavigate();
  const t = (key: string) => (translations ? translations(key) : fallbackTranslations[key] || key);

  return (
    <div className={cn('w-full h-svh', className)}>
      <div className="flex flex-col gap-2 justify-center items-center m-auto w-full h-full">
        {!minimal && <h1 className="text-[7rem] font-bold leading-tight">500</h1>}
        <span className="font-medium">{t('error.500_title')}</span>
        <p className="text-center text-muted-foreground">{t('error.500_desc')}</p>
        {!minimal && (
          <div className="flex gap-4 mt-6">
            <Button variant="outline" onClick={() => navigate(-1)}>
              {t('error.go_back')}
            </Button>
            <Button onClick={() => navigate('/')}>{t('error.back_to_home')}</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function GeneralErrorWithTranslations(props: GeneralErrorProps) {
  const translations = useTranslations();
  return <GeneralErrorContent {...props} translations={translations} />;
}

export default function GeneralError(props: GeneralErrorProps) {
  // Try to render with translations, fall back to without if context is missing
  try {
    return <GeneralErrorWithTranslations {...props} />;
  } catch {
    return <GeneralErrorContent {...props} />;
  }
}
